import { useEffect, useState } from 'react';
import { adminAPI, fmtError } from '@/lib/api';
import { Ban, RotateCcw } from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => adminAPI.users().then(setUsers);
  useEffect(() => { load(); }, []);
  const filtered = users.filter(u => !q || (u.email + u.name + u.referral_code).toLowerCase().includes(q.toLowerCase()));

  const terminate = async () => {
    setBusy(true); setErr('');
    try {
      await adminAPI.terminateUser(modal.user_id, reason.trim());
      setModal(null); setReason('');
      load();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  const reinstate = async (uid) => {
    if (!window.confirm('Reinstate this user? They will regain purchase + payout access.')) return;
    try { await adminAPI.reinstateUser(uid); load(); }
    catch (e) { alert(fmtError(e)); }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl">Users</h1>
      <input className="input-tech max-w-md" placeholder="Search email, name, referral code…" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-users-search" />
      <div className="card-tech rounded-lg overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-[1.4fr_1.8fr_1fr_1fr_1fr_1.2fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
            <div>NAME</div><div>EMAIL</div><div>ROLE</div><div>TIER</div><div>REF</div><div className="text-right">ACTIONS</div>
          </div>
          {filtered.map(u => {
            const t = !!u.is_terminated;
            return (
              <div key={u.user_id}
                   className={`grid grid-cols-[1.4fr_1.8fr_1fr_1fr_1fr_1.2fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center ${t ? 'bg-[#ff3366]/[0.04]' : ''}`}
                   data-testid={`admin-user-${u.user_id}`}>
                <div className="flex items-center gap-2">
                  <span>{u.name}</span>
                  {t && (
                    <span className="chip chip-red text-[9px] font-bold" data-testid={`user-terminated-tag-${u.user_id}`}>
                      <Ban size={9} className="inline mr-1" /> TERMINATED
                    </span>
                  )}
                </div>
                <div className="text-zinc-400 truncate">{u.email}</div>
                <div><span className={`chip ${u.role === 'admin' ? 'chip-green' : ''}`}>{(u.role || '').toUpperCase()}</span></div>
                <div className="font-mono">{(u.affiliate_tier || '').toUpperCase()}</div>
                <div className="font-mono text-xs truncate">{u.referral_code}</div>
                <div className="flex justify-end gap-2">
                  {u.role !== 'admin' && (t
                    ? <button className="btn-ghost btn-sm" onClick={() => reinstate(u.user_id)} data-testid={`reinstate-user-${u.user_id}`}>
                        <RotateCcw size={11} className="inline mr-1" /> Reinstate
                      </button>
                    : <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => { setModal(u); setReason(''); setErr(''); }} data-testid={`terminate-user-${u.user_id}`}>
                        <Ban size={11} className="inline mr-1" /> Terminate
                      </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="card-tech rounded-lg p-6 max-w-md w-full border border-[#ff3366]/40" onClick={e => e.stopPropagation()}>
            <div className="font-mono text-[10px] tracking-[3px] text-[#ff3366]">RISK &amp; COMPLIANCE</div>
            <h3 className="font-display text-2xl mt-1 text-[#ff3366]">Terminate account</h3>
            <p className="text-sm text-zinc-400 mt-2">
              <span className="text-white">{modal.name}</span> · <span className="font-mono">{modal.email}</span>
            </p>
            <p className="text-xs text-zinc-500 mt-3">
              User will keep login access but will be blocked from new purchases, phase upgrades, payouts and affiliate withdrawals.
              All pending requests will be auto-declined. Reversible.
            </p>
            <div className="label-xs mt-4 mb-1">Reason (shown on their dashboard)</div>
            <textarea className="input-tech" rows={3}
                      placeholder="e.g. Serious misconduct — infrastructure exploitation"
                      value={reason} onChange={e => setReason(e.target.value)}
                      data-testid="terminate-reason" />
            {err && <div className="text-sm text-[#ff3366] mt-2">{err}</div>}
            <div className="flex gap-2 mt-4 justify-end">
              <button className="btn-ghost" onClick={() => setModal(null)} disabled={busy}>Cancel</button>
              <button className="btn-primary" style={{ background: '#ff3366', color: '#fff' }}
                      onClick={terminate} disabled={busy}
                      data-testid="confirm-terminate">
                {busy ? 'Terminating…' : 'Confirm termination'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
